// @flow

export const cropImage = (
  event: ?SyntheticEvent<any, any>,
  file: File,
  width: number,
  height: number,
  posX: number = 0,
  posY: number = 0,
  quality?: number,
  maxWidth?: number,
  maxHeight?: number,
  keepFormat?: boolean
): Promise<[string, File]> => {
  return new Promise((res, rej) => {
    if (event) event.persist();
    const fileName = file.name.split('.')[0];
    const imageElement = new Image();
    imageElement.src = URL.createObjectURL(file);
    imageElement.onload = e => {
      const canvas = document.createElement('canvas');
      let scaleSizeHorizontal = 1;
      let scaleSizeVertical = 1;
      if (maxWidth && maxHeight) {
        scaleSizeHorizontal = maxWidth / width;
        scaleSizeVertical = maxHeight / height;
        if (width < maxWidth && height < maxHeight) {
          canvas.width = width;
          canvas.height = height;
          scaleSizeHorizontal = 1;
          scaleSizeVertical = 1;
        } else if (scaleSizeHorizontal > scaleSizeVertical) {
          canvas.width = width * scaleSizeVertical;
          canvas.height = maxHeight;
          scaleSizeHorizontal = 1;
        } else {
          canvas.width = maxWidth;
          canvas.height = height * scaleSizeHorizontal;
          scaleSizeVertical = 1;
        }
      } else {
        canvas.width = width;
        canvas.height = height;
      }

      const ctx = canvas.getContext('2d');
      const extension = keepFormat ? file.name.split('.').pop() : 'jpg';
      const type = keepFormat ? file.type : 'image/jpeg';

      if (extension === 'jpg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(
        imageElement, // image
        posX * scaleSizeHorizontal, // sourceImageX
        posY * scaleSizeVertical, // sourceImageY
        width, // sourceImageX
        height, // sourceImageY
        0, // destinationX
        0, // destinationY
        canvas.width, // destinationWidth
        canvas.height // destinationHeight
      );

      canvas.toBlob(
        blob => {
          const croppedFile = new File([blob], fileName + `.${extension}`);
          res([canvas.toDataURL(), croppedFile]);
        },
        type,
        quality
      );
    };
  });
};

export const crop = (
  event: ?SyntheticEvent<any, any>,
  file: File,
  width: number,
  height: number
): Promise<[string, File]> => {
  return new Promise((res, rej) => {
    if (event) event.persist();
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = readerEvent => {
      const target = readerEvent.target;
      const imageElement = document.createElement('img');
      imageElement.src = (target: any).result;
      imageElement.onload = e => {
        const canvas = document.createElement('canvas');
        const scaleSizeHorizontal = width / e.target.width;
        const scaleSizeVertical = height / e.target.height;
        if (e.target.width < width && e.target.height < height) {
          canvas.width = e.target.width;
          canvas.height = e.target.height;
        } else if (scaleSizeHorizontal > scaleSizeVertical) {
          canvas.width = e.target.width * scaleSizeVertical;
          canvas.height = height;
        } else {
          canvas.width = width;
          canvas.height = e.target.height * scaleSizeHorizontal;
        }
        /*canvas.width = e.target.width < width ? e.target.width : width;
          canvas.height =
            typeof height === 'number' ? height : e.target.height * scaleSize;*/
        // const newImage = new Image();
        const ctx = canvas.getContext('2d');
        ctx.drawImage(e.target, 0, 0, canvas.width, canvas.height);
        // res(canvas.toDataURL());
        canvas.toBlob(blob => {
          const croppedFile = new File([blob], file.name);
          // const srcEncoded = ctx.canvas.toDataURL(e.target, file.type);
          res([canvas.toDataURL(), croppedFile]);
        }, file.type);
      };
    };
    reader.onerror = _ => {
      reader.abort();
      rej(new Error('There was an error compressing the image'));
    };
  });
};

export const compressImage = (
  event: ?SyntheticEvent<any, any>,
  file: File,
  quality: number,
  maxWidth?: number,
  maxHeight?: number
): Promise<[string, File]> => {
  return new Promise((res, rej) => {
    if (event) event.persist();
    var fileName = file.name.split('.')[0];
    const imageElement = new Image();
    imageElement.src = URL.createObjectURL(file);
    imageElement.onload = e => {
      const canvas = document.createElement('canvas');

      if (maxWidth && maxHeight) {
        const scaleSizeHorizontal = maxWidth / imageElement.width;
        const scaleSizeVertical = maxHeight / imageElement.height;
        if (imageElement.width < maxWidth && imageElement.height < maxHeight) {
          canvas.width = imageElement.width;
          canvas.height = imageElement.height;
        } else if (scaleSizeHorizontal > scaleSizeVertical) {
          canvas.width = imageElement.width * scaleSizeVertical;
          canvas.height = maxHeight;
        } else {
          canvas.width = maxWidth;
          canvas.height = imageElement.height * scaleSizeHorizontal;
        }
      } else {
        canvas.width = imageElement.width;
        canvas.height = imageElement.height;
      }

      const ctx = canvas.getContext('2d');

      // to avoid the black background when the image is transparent
      ctx.fillStyle = '#FFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        blob => {
          const compressedFile = new File([blob], fileName + '.jpeg');
          res([canvas.toDataURL(), compressedFile]);
        },
        'image/jpeg',
        quality
      );
    };
  });
};

export type CompressImageAdvancedParams = {
  quality: number,
  maxWidth?: number,
  maxHeight?: number,
  customFormat?: 'png' | 'jpeg' | 'webp',
};

type ResizedDimensionsParams = {
  width: number,
  height: number,
  maxWidth?: number,
  maxHeight?: number,
};

const getResizedDimensions = ({
  width,
  height,
  maxWidth,
  maxHeight,
}: ResizedDimensionsParams): {
  scaledWidth: number,
  scaledHeight: number,
} => {
  const scaleFactor = Math.min(
    maxWidth ? Math.min(maxWidth / width, 1) : 1,
    maxHeight ? Math.min(maxHeight / height, 1) : 1
  );

  return {
    scaledWidth: width * scaleFactor,
    scaledHeight: height * scaleFactor,
  };
};

export const compressImageAdvanced = (
  file: File,
  params: CompressImageAdvancedParams
): Promise<[string, File]> => {
  if (!file.type.startsWith('image/')) {
    return Promise.reject(
      new Error('El archivo proporcionado no es una imagen.')
    );
  }

  const {maxHeight, maxWidth, quality, customFormat} = params;
  const [originalName] = file.name.split('.');
  const originalExtension = file.name
    .substring(file.name.lastIndexOf('.') + 1)
    .toLowerCase();

  const FILE_NAME = `${originalName}.${customFormat || originalExtension}`;
  const FILE_TYPE = customFormat ? `image/${customFormat}` : file.type;

  return new Promise((res, rej) => {
    const imageElement = new Image();
    imageElement.src = URL.createObjectURL(file);

    imageElement.onload = () => {
      URL.revokeObjectURL(imageElement.src);
      const canvas = document.createElement('canvas');

      const {scaledWidth, scaledHeight} = getResizedDimensions({
        width: imageElement.width,
        height: imageElement.height,
        maxWidth,
        maxHeight,
      });

      canvas.width = scaledWidth;
      canvas.height = scaledHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return rej(new Error('No se pudo obtener el contexto del canvas.'));
      }

      if (FILE_TYPE === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        blob => {
          if (!blob) {
            return rej(new Error('La compresión de la imagen falló.'));
          }
          const file = new File([blob], FILE_NAME, {type: FILE_TYPE});
          const preview = canvas.toDataURL(FILE_TYPE, quality);
          res([preview, file]);
        },
        FILE_TYPE,
        quality
      );
    };

    imageElement.onerror = () => {
      URL.revokeObjectURL(imageElement.src);
      rej(new Error('Error al cargar la imagen para la compresión.'));
    };
  });
};

export type CompressImageIfNecessaryParams = {
  threshold: number,
} & CompressImageAdvancedParams;

export async function compressImageIfNecessary(
  file: File,
  params: CompressImageIfNecessaryParams
): Promise<File> {
  const {threshold, maxWidth, maxHeight, customFormat} = params;
  const originalSize = file.size;

  let quality = 1;
  const minimumQuality = 0.85;

  if (originalSize >= threshold) {
    while (quality >= minimumQuality) {
      try {
        const [, image] = await compressImageAdvanced(file, {
          quality,
          maxWidth,
          maxHeight,
          customFormat,
        });

        if (image.size < threshold || quality <= 0.1) {
          return image;
        }

        quality -= 0.05;
        file = image;
      } catch (error) {
        console.log(error);
        return file;
      }
    }
    return file;
  } else {
    return file;
  }
}

export const cropImageEvent = (
  event: SyntheticEvent<any, any>,
  width: number,
  height: number
): Promise<[string, File]> => {
  if (event.target instanceof HTMLInputElement) {
    const file = event.target.files[0];
    if (!file) return Promise.reject(new Error('No file was provided'));
    return crop(event, file, width, height);
  } else {
    return Promise.reject('Input was not a valid DOM element');
  }
};

export const cropImageFile = (
  file: File,
  width: number,
  height: number
): Promise<[string, File]> => {
  return crop(undefined, file, width, height);
};

export function getImage(
  url: string
): Promise<{image: Image, width: number, height: number}> {
  return new Promise((resolve, reject) => {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    img.onload = function() {
      resolve({image: img, width: this.width, height: this.height});
    };
    img.onerror = function() {
      reject();
    };
  });
}

export async function createFile(
  url: string,
  name?: string = 'test'
): Promise<{file: File}> {
  let response = await fetch(url);
  let data = await response.blob();
  let metadata = {
    type: 'image/jpeg',
  };
  let file = new File([data], `${name}.jpg`, metadata);

  return {file};
}
